import { useNavigate } from 'react-router-dom';
import {
    Plus, AlertCircle, ArrowRight, LayoutList, FileWarning,
    Gauge, HardHatIcon, Wrench
} from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import LinedMoldIcon from '../icons/LinedMoldIcon.tsx';
import OutlinedMoldIcon from '../icons/OutlinedMoldIcon.tsx';
import QrCodeSearchStack from '../icons/QrCodeSearchStack';
import { getIconComponent } from '../../utils/icon.tsx';
import { ExtendedMoldOrderFormConfig } from '../../models/appState';
import { MoldOrderStats, MoldStats } from '../../types/statistics.ts';
import { useAppState } from '../../context/AppStateContext.tsx';

interface MoldStatsProps {
    moldOrderGroups: ExtendedMoldOrderFormConfig[];
    loading: boolean;
    moldStats: MoldStats | null;
    moldOrderStats: MoldOrderStats | null;
    statsAvailable: boolean;
    ordersAvailable: boolean;
}

/**
 * Layout Configuration for Work Order Cards
 * 
 * Easily customize the responsive layout based on the number of visible work order cards.
 * 
 * Format: 'grid-cols-{mobile} sm:grid-cols-{tablet} lg:grid-cols-{desktop}'
 * 
 * Breakpoints:
 * - Mobile: < 640px (default)
 * - sm (Tablet): ≥ 640px
 * - lg (Desktop): ≥ 1024px
 * 
 * Note: The Mold Summary Card always takes half the screen on desktop (lg:grid-cols-2).
 * These settings only affect the work order cards in the right half.
 * 
 * Examples:
 * - 1 card: Single column on all screens
 * - 2 cards: Stack on mobile, 2 columns on tablet and desktop
 * - 3 cards: Stack on mobile, 3 columns on tablet and desktop
 */
const WORK_ORDER_CARD_LAYOUTS = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-3 lg:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-2',
} as const;

const getCardLayoutClasses = (cardCount: number): string => {
    // Return the specific layout or default to the 3-card layout for 3+ cards
    return WORK_ORDER_CARD_LAYOUTS[cardCount as keyof typeof WORK_ORDER_CARD_LAYOUTS] 
        || WORK_ORDER_CARD_LAYOUTS[3];
};

const MoldStatsSection: React.FC<MoldStatsProps> = ({
    loading,
    moldStats,
    moldOrderGroups,
    moldOrderStats,
    ordersAvailable,
    statsAvailable,
}) => {
    const navigate = useNavigate();
    const { mold: { statusLabelMapping }, homepage: { workOrderCards } } = useAppState();

    const needAttentionMoldCount = statsAvailable ? moldStats!.attentionRequiredMolds : 'N/A';
    const inServicingMoldsCount = statsAvailable ? moldStats!.inServicingMolds : 'N/A';
    const moldsTotal = statsAvailable ? moldStats!.totalMolds : 'N/A';

    // Filter and map work order cards based on configuration
    const moldOrderGroupsWithCount = workOrderCards
        .filter(card => card.show) // Only show cards marked as visible
        .map(cardConfig => {
            // Find the corresponding mold order group
            const group = moldOrderGroups.find(g => g.key === cardConfig.id);

            if (!group) {
                return null;
            }

            const count = ordersAvailable
                ? (moldOrderStats!.openOrders ?? [])
                    .filter(o => group.moldOrderTypes.includes((o.orderType ?? '') as any))
                    .reduce((s, o) => s + (o.count || 0), 0)
                : 'N/A';

            return {
                ...group,
                id: cardConfig.id,
                title: cardConfig.title,
                subtitle: group.isEnabled ? cardConfig.subtitle : (cardConfig.disabledSubtitle || 'ยังไม่เปิดใช้งาน'),
                count,
                route: cardConfig.route || `${ROUTES.DASHBOARD}?boardType=${cardConfig.id}`,
            };
        })
        .filter((card): card is NonNullable<typeof card> => card !== null); // Remove null entries with type guard

    // Get the appropriate grid layout based on number of cards
    const cardLayoutClasses = getCardLayoutClasses(moldOrderGroupsWithCount.length);

    return (
        <>
            {/* Mold Statistics Header */}
            <div className="flex items-center mb-4">
                <OutlinedMoldIcon color="#2563eb" size={36} strokeWidth={60} showHolders={false} />
                <h2 className="pl-3 text-lg sm:text-2xl font-bold text-gray-900">ข้อมูลสถานะระบบ</h2>
                <button
                    onClick={() => navigate(ROUTES.MOLDS)}
                    className="ml-auto text-blue-500 hover:text-blue-700 flex items-center text-sm"
                >
                    ดูทั้งหมด <ArrowRight className="w-4 h-4 ml-1" />
                </button>
            </div>

            {/* Combined Statistics in Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Mold Summary Card */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-blue-100 col-span-1">
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b border-blue-100">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
                            <div className="flex items-center cursor-pointer" onClick={() => navigate(ROUTES.MOLDS)}>
                                <div className="bg-white p-2 rounded-full shadow-sm">
                                    <LinedMoldIcon size={24} strokeWidth={2} />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-base font-bold text-gray-800">แม่พิมพ์ทั้งหมด</h3>
                                    {loading ? (
                                        <div className="animate-pulse h-6 w-16 bg-gray-200 rounded"></div>
                                    ) : (
                                        <p className="text-2xl font-bold text-blue-600">
                                            {moldsTotal} <span className="text-xs font-normal text-gray-600">รายการ</span>
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Button group for actions - Modified for tablet display */}
                            <div className="flex gap-4 w-full md:w-auto mt-4 md:mt-0">
                                {/* Scan QR Button - Bigger and Square */}
                                <button
                                    onClick={() => navigate(ROUTES.SCAN_MOLD)}
                                    className="flex flex-1 md:flex-auto flex-col items-center justify-center 
                                                        bg-white border-2 border-blue-300 text-blue-600 
                                                        hover:bg-blue-50 p-3 rounded-lg shadow-sm 
                                                        hover:shadow-md transition-all aspect-square"
                                    title="ค้นหาแม่พิมพ์ด้วย QR Code"
                                >
                                    <QrCodeSearchStack size={28} className="mb-2" />
                                    <span className="text-xs font-medium">ค้นหาด้วย QR</span>
                                </button>

                                {/* Create New Mold Button - Bigger and Square */}
                                <button
                                    onClick={() => navigate(ROUTES.CREATE_NEW_MOLD)}
                                    className="flex flex-1 md:flex-auto flex-col items-center justify-center 
                                                        bg-blue-500 hover:bg-blue-600 text-white 
                                                        p-3 rounded-lg shadow-sm hover:shadow-md 
                                                        transition-all aspect-square"
                                    title="เพิ่มแม่พิมพ์"
                                >
                                    <Plus className="w-7 h-7 mb-2" />
                                    <span className="text-xs font-medium">เพิ่มแม่พิมพ์</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mold status indicators */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                        {loading ? (
                            <div className="p-4 flex justify-center items-center">
                                <div className="animate-pulse flex space-x-4">
                                    <div className="rounded-full bg-slate-200 h-8 w-8"></div>
                                    <div className="flex-1 space-y-2">
                                        <div className="h-3 bg-slate-200 rounded"></div>
                                        <div className="h-3 bg-slate-200 rounded w-5/6"></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Attention required molds */}
                                <div className="p-3 bg-red-50/60" data-testid="attention-required-card">
                                    <div className="flex items-center">
                                        <div className="mr-3">
                                            <div className="bg-red-100 p-2 rounded-full">
                                                <FileWarning className="h-5 w-5 text-red-500" />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-baseline">
                                                <p className="text-xl font-bold text-red-600">{needAttentionMoldCount}</p>
                                                <p className="text-xs ml-1 text-gray-700">แม่พิมพ์</p>
                                            </div>
                                            <p className="text-xs font-medium text-red-800">ถึงรอบดูแลแล้ว</p>
                                        </div>
                                        {typeof needAttentionMoldCount === 'number' && needAttentionMoldCount > 0 && (
                                            <div className="ml-auto">
                                                <div className="animate-pulse bg-red-100 p-1 rounded-full">
                                                    <AlertCircle className="h-4 w-4 text-red-500" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <p className="mt-2 text-xs text-gray-600">
                                        จำนวนช็อตถึงเกณฑ์แจ้งเตือน ควรเปิดใบงานล้างแม่พิมพ์
                                    </p>
                                    <div className="mt-2 flex items-center">
                                        <div className="h-1.5 bg-red-100 rounded-full w-full">
                                            <div className="h-1.5 bg-red-500 rounded-full" style={{
                                                width: typeof needAttentionMoldCount === 'number' && typeof moldsTotal === 'number'
                                                    ? `${Math.min(100, (needAttentionMoldCount / Math.max(1, moldsTotal)) * 100)}%`
                                                    : '0%'
                                            }}></div>
                                        </div>
                                        <span className="ml-1 text-xs text-gray-500">{
                                            (typeof needAttentionMoldCount === 'number' && typeof moldsTotal === 'number')
                                                ? `${Math.round((needAttentionMoldCount / Math.max(1, moldsTotal)) * 100)}%`
                                                : 'N/A'
                                        }</span>
                                    </div>
                                    <div className="mt-3 grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => navigate(`${ROUTES.MOLDS}?status=ATTENTION_REQUIRED`)}
                                            className="rounded-lg border border-red-200 bg-white px-2 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100"
                                        >
                                            ดูรายการ
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => navigate(`${ROUTES.CREATE_MAINTENANCE_ORDERS}&attention=true`)}
                                            disabled={typeof needAttentionMoldCount !== 'number' || needAttentionMoldCount === 0}
                                            className="inline-flex items-center justify-center gap-1 rounded-lg bg-red-600 px-2 py-2 text-xs font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                                        >
                                            <Wrench className="h-3.5 w-3.5" aria-hidden="true" />
                                            เปิดใบงานล้าง
                                        </button>
                                    </div>
                                </div>

                                {/* Critical molds */}
                                <div onClick={() => navigate(`${ROUTES.MOLDS}?status=IN_SERVICING`)}
                                    className="p-3 cursor-pointer hover:bg-amber-50 transition-colors">
                                    <div className="flex items-center">
                                        <div className="mr-3">
                                            <div className="bg-amber-100 p-2 rounded-full">
                                                <Gauge className="h-5 w-5 text-amber-500" />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-baseline">
                                                <p className="text-xl font-bold text-amber-600">{inServicingMoldsCount}</p>
                                                <p className="text-xs ml-1 text-gray-700">แม่พิมพ์</p>
                                            </div>
                                            <p className="text-xs text-gray-700">ที่{statusLabelMapping['in_servicing']}</p>
                                        </div>
                                        {typeof inServicingMoldsCount === 'number' && inServicingMoldsCount > 0 && (
                                            <div className="ml-auto">
                                                <div className="animate-pulse bg-amber-100 p-1 rounded-full">
                                                    <AlertCircle className="h-4 w-4 text-amber-500" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-2 flex items-center">
                                        <div className="h-1.5 bg-amber-100 rounded-full w-full">
                                            <div className="h-1.5 bg-amber-500 rounded-full" style={{
                                                width: typeof inServicingMoldsCount === 'number' && typeof moldsTotal === 'number'
                                                    ? `${Math.min(100, (inServicingMoldsCount / Math.max(1, moldsTotal)) * 100)}%`
                                                    : '0%'
                                            }}></div>
                                        </div>
                                        <span className="ml-1 text-xs text-gray-500">{
                                            (typeof inServicingMoldsCount === 'number' && typeof moldsTotal === 'number')
                                                ? `${Math.round((inServicingMoldsCount / Math.max(1, moldsTotal)) * 100)}%`
                                                : 'N/A'
                                        }</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* View All Molds Button */}
                    <div className="border-t border-gray-100 p-3">
                        <button
                            onClick={() => navigate(ROUTES.MOLDS)}
                            className="w-full flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-700 px-4 py-2 rounded-lg transition-colors font-medium"
                        >
                            <LayoutList className="h-4 w-4" />
                            <span>ดูรายการแม่พิมพ์ทั้งหมด</span>
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Work Order Cards Container - Takes the other half on desktop */}
                <div className={`col-span-1 grid gap-3 ${cardLayoutClasses}`}>
                    {/* Work Order Cards - Dynamic Generation */}
                    {moldOrderGroupsWithCount.map((card) => {
                        const IconComponent = card.uiConfig ? getIconComponent(card.uiConfig) ?? HardHatIcon : HardHatIcon;
                        const colorCode = card.isEnabled ? 'blue' : 'gray';
                        const iconProps = (card.isEnabled ? card.uiConfig?.icon.props : card.uiConfig?.icon.disabledProps) ?? {};
                        return (
                            <div
                                key={card.id}
                                onClick={card.isEnabled ? () => navigate(card.route) : undefined}
                                className={`group rounded-xl transition-colors shadow-sm flex flex-col items-center justify-center p-4
                                            cursor-pointer border border-${colorCode}-200/50 bg-${colorCode}-50 hover:bg-${colorCode}-200`}
                            >
                                <div className={`p-2 rounded-full mb-2 bg-${colorCode}-100`}>
                                    <IconComponent
                                        className={`h-6 w-6 text-${colorCode}-500`}
                                        {...(iconProps)} />
                                </div>
                                <div className={`text-2xl font-bold text-${colorCode}-700`}>
                                    {card.isEnabled ? card.count : '-'}
                                </div>
                                <div className={`text-sm font-semibold text-${colorCode}-700`}>
                                    {card.title}
                                </div>
                                <div className={'text-xs text-gray-500'}>
                                    {card.isEnabled ? card.subtitle : 'ยังไม่เปิดใช้งาน'}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    )
}

export default MoldStatsSection;
